import React, { useState, useEffect } from 'react';

export const PwaInstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isStandalone, setIsStandalone] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isIos, setIsIos] = useState<boolean>(false);

  useEffect(() => {
    // Check if already installed / standalone
    if (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true
    ) {
      setIsStandalone(true);
    }

    // Detect iOS
    const userAgent = window.navigator.userAgent.toLowerCase();
    const iosDevice = /iphone|ipad|ipod/.test(userAgent);
    setIsIos(iosDevice);

    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
      }
    } else {
      setShowModal(true);
    }
  };

  if (isStandalone) return null;

  return (
    <>
      {/* Top Header Floating PWA Install Pill */}
      <button
        onClick={handleInstallClick}
        className="px-3 py-1.5 bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-[#0F5132] border border-amber-600 rounded-full text-xs font-black shadow-md flex items-center gap-1.5 cursor-pointer transition-transform hover:scale-105 active:scale-95"
        title="Install ERUE Festival App on mobile or desktop device"
      >
        <span className="text-sm">📱</span>
        <span>Install App</span>
      </button>

      {/* PWA Instruction Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-amber-400 animate-in fade-in zoom-in duration-200">
            
            {/* Header */}
            <div className="bg-[#0F5132] text-white p-4 flex items-center justify-between border-b border-amber-400/30">
              <div className="flex items-center gap-2">
                <img
                  src="https://res.cloudinary.com/uuovmrek/image/upload/uzairuelogo01"
                  alt="App Icon"
                  className="w-8 h-8 rounded-full border border-amber-400 object-contain"
                  referrerPolicy="no-referrer"
                />
                <div>
                  <h3 className="font-serif font-black text-sm text-amber-200">
                    Install ERUE Festival 2026 App
                  </h3>
                  <p className="text-[10px] text-emerald-100">Cross-platform desktop & mobile experience</p>
                </div>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 text-white font-bold flex items-center justify-center text-xs cursor-pointer"
              >
                ✕
              </button>
            </div>

            {/* Body Instructions */}
            <div className="p-5 space-y-4 text-xs text-gray-700">
              {isIos ? (
                <div className="space-y-3 bg-amber-50 p-4 rounded-xl border border-amber-200">
                  <p className="font-bold text-[#0F5132]">To Install on iOS (iPhone / iPad):</p>
                  <ol className="list-decimal list-inside space-y-2 text-xs">
                    <li>Tap the <strong className="text-blue-600">Share button</strong> (square with arrow up) at the bottom of Safari.</li>
                    <li>Scroll down and tap <strong className="text-amber-800">Add to Home Screen ➕</strong>.</li>
                    <li>Tap <strong className="text-[#0F5132]">Add</strong> in the top right corner.</li>
                  </ol>
                </div>
              ) : (
                <div className="space-y-3 bg-emerald-50 p-4 rounded-xl border border-emerald-200">
                  <p className="font-bold text-[#0F5132]">To Install on Desktop or Android:</p>
                  <ol className="list-decimal list-inside space-y-2 text-xs">
                    <li>Look at your browser menu (⋮ three dots in top right).</li>
                    <li>Select <strong className="text-emerald-900">Install ERUE Festival 2026</strong> or <strong className="text-emerald-900">Add to Home Screen</strong>.</li>
                    <li>Launch the app anytime from your desktop or phone home screen!</li>
                  </ol>
                </div>
              )}

              <div className="p-3 bg-gray-50 rounded-lg text-[11px] text-gray-600 flex items-center gap-2">
                <span>⚡</span>
                <span>Works offline, launches faster, and provides full-screen document printing & signing capabilities.</span>
              </div>
            </div>

            {/* Footer */}
            <div className="p-3 bg-gray-50 border-t text-right">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-[#0F5132] text-amber-300 font-bold rounded-lg text-xs hover:bg-[#0c4028] cursor-pointer"
              >
                Got It
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
};
